import { useMemo } from 'react';

import { useAnalytics } from '@backstage/core-plugin-api';

import {
  ErrorCode,
  ReactionSummaryFragment,
  UndoReactionMutation,
  getTypeOrNull,
} from '@spotify/confidence-graphql';
import { useCurrentIdentity } from '@spotify/plugin-confidence-iam-react';

import { useCreateReaction } from '../useCreateReaction';
import { useUndoReaction } from '../useUndoReaction';

/**
 * Hook for managing reactions for a given resource.
 * @param resource - The resource to manage reactions for.
 * @param summary - The reaction summary to manage.
 * @returns An object with the following properties:
 *  - reactedEmojis: A set of emojis that the current user has reacted with.
 *  - toggleReaction: A function that toggles a reaction for a given emoji.
 */
export const useReactions = (
  resource: string,
  summary: ReactionSummaryFragment | null | undefined,
) => {
  const analytics = useAnalytics();
  const { currentIdentity } = useCurrentIdentity();

  const { createReaction } = useCreateReaction({
    optimisticResponse: ({ input }) => ({
      createReaction: {
        __typename: 'CollaborationV1Reaction',
        name: `reactions/${input.resource}-temp`, // Temporary name, will be replaced by server
        emoji: input.emoji,
        resource: input.resource,
        createTime: new Date().toISOString(),
        updateTime: new Date().toISOString(),
        author: currentIdentity
          ? currentIdentity
          : {
              __typename: 'Error',
              message: 'No identity',
              code: ErrorCode.Unknown,
            },
      },
    }),
  });

  const { undoReaction } = useUndoReaction({
    optimisticResponse: (): UndoReactionMutation => ({
      undoReaction: {
        __typename: 'CollaborationV1UndoReactionResponse',
        dummy: null,
      },
    }),
  });

  const emojiReactions = useMemo(
    () => summary?.emojiReactions ?? [],
    [summary],
  );

  // Compute which emojis the current user has reacted with
  const reactedEmojis = useMemo(() => {
    const reacted = new Set<string>();
    if (!currentIdentity?.name) return reacted;

    for (const reaction of emojiReactions) {
      const emoji = reaction.value?.emoji ?? reaction.key;
      const hasReacted = reaction.value?.identities?.some(
        identity => getTypeOrNull(identity)?.name === currentIdentity.name,
      );
      if (hasReacted) {
        reacted.add(emoji);
      }
    }
    return reacted;
  }, [emojiReactions, currentIdentity?.name]);

  const toggleReaction = (emoji: string) => {
    if (reactedEmojis.has(emoji)) {
      undoReaction({ resource, emoji });
      analytics.captureEvent('emoji-reaction-remove', resource, {
        attributes: {
          emoji,
        },
      });
    } else {
      createReaction({ emoji, resource }, undefined);
      analytics.captureEvent('emoji-reaction-add', resource, {
        attributes: {
          emoji,
        },
      });
    }
  };

  return {
    reactedEmojis,
    toggleReaction,
    emojiReactions,
  };
};
